import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { SpinningIcon } from '@/components/animation/SpinningIcon/SpinningIcon';
import { BoxedIcon } from '@/components/display/Ribbon/BoxedIcon';
import { SmallTableEmptyState } from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { Button } from '@/components/form/baseInputs/Button';
import { BankIcon } from '@/components/icons/BankIcon';
import { Building07Icon } from '@/components/icons/Building07Icon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useModalState } from '@/hooks/useModalState';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { AIOnboardingModal } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal';
import {
  AIOnboardingModalVariant,
  OpenAIOnboardingModalProps,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { AISuggestionReviewCard } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { BUSINESS_ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';
import { useEstateHoldingsContext } from '@/modules/estate/contexts/estateHoldings.context';
import { useEstateOnboardingUnacknowledgedSuggestionsContext } from '@/modules/estate/estateAIOnboarding/context/estateOnboardingUnacknowledgedSuggestions.context';
import { EstateOnboardingSuggestionGroup } from '@/modules/estate/estateAIOnboarding/estateOnboarding.types';
import { EstateBusinessEntityCard } from '@/modules/estate/EstateBusinesses/EstateBusinessEntityCard';
import { sumGrantorControlledValue } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupContainer.utils';
import { filterBusinesses } from '@/modules/estate/EstateDirectHoldings/EstateDirectHoldings.utils';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

export function EstateBusinesses() {
  const householdId = useRequiredParam('householdId');
  const canEdit = useCanEditHouseholdData();
  const { loading, entities } = useEstateHoldingsContext();
  const {
    suggestionsToReviewByGroup,
    additionalReferenceSuggestions,
    refetch: refetchSuggestions,
  } = useEstateOnboardingUnacknowledgedSuggestionsContext();

  const businessEntitySuggestions =
    suggestionsToReviewByGroup[
      EstateOnboardingSuggestionGroup.BusinessEntities
    ] || [];

  const [aiOnboardingState, aiOnboardingModal] =
    useModalState<
      Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>
    >();

  const handleOpenAIOnboardingModal = useCallback(
    ({
      initialSelectedSuggestion,
    }: Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>) => {
      aiOnboardingModal.openModal({ initialSelectedSuggestion });
    },
    [aiOnboardingModal]
  );
  const businesses = useMemo(() => {
    const filteredBusinesses = filterBusinesses(entities ?? []);

    // Sort businesses by descending order of net value (highest to lowest)
    return filteredBusinesses.sort((a, b) => {
      const valueA = sumGrantorControlledValue([a]);
      const valueB = sumGrantorControlledValue([b]);
      return valueB.cmp(valueA); // Descending order (highest first)
    });
  }, [entities]);

  const [showCreateBusinessModal, setShowCreateBusinessModal] = useState(false);
  const hasExistingData = (entities?.length ?? 0) > 0;

  if (loading && !hasExistingData) {
    return (
      <Stack
        height="40px"
        direction="row"
        alignItems="center"
        justifyContent="center"
      >
        <SpinningIcon sx={{ color: COLORS.TEXT.SECONDARY }} />
      </Stack>
    );
  }

  // Show suggestions even if there are no existing businesses
  const hasBusinessSuggestions = businessEntitySuggestions.length > 0;
  const currentValue = sumGrantorControlledValue(businesses);

  return (
    <Stack gap={2}>
      {hasBusinessSuggestions && canEdit && (
        <AISuggestionReviewCard
          variant={AIOnboardingModalVariant.BUSINESS_ENTITIES}
          suggestionDisplayText={{
            singular: 'business',
            plural: 'businesses',
          }}
          suggestions={businessEntitySuggestions}
          additionalReferenceSuggestions={additionalReferenceSuggestions}
          onOpenAIOnboardingModal={handleOpenAIOnboardingModal}
          // No need to show the processing banner here since the whole
          // "/estate" outlet already has one in EstateOnboardingSuggestionsBanner
          showProcessingBanner={false}
          onSuggestionCreated={refetchSuggestions}
        />
      )}
      {businesses.length === 0 ? (
        <SmallTableEmptyState
          fill="viewport"
          icon={<BankIcon size={32} color={COLORS.TEXT.PRIMARY} />}
          heading="No businesses"
          description="There are no businesses associated with this client"
          action={
            canEdit ? (
              <Button
                variant="primary"
                size="sm"
                startIcon={PlusIcon}
                onClick={() => setShowCreateBusinessModal(true)}
              >
                Create business
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <Stack direction="row" spacing={2} alignItems="center">
            <BoxedIcon
              icon={Building07Icon}
              sx={{
                backgroundColor: COLORS.NAVY[100],
              }}
              iconProps={{
                size: 24,
                color: COLORS.NAVY[700],
                strokeWidth: 1.67,
              }}
            />
            <Stack>
              <Typography variant="title-s" component="div">
                Businesses
              </Typography>
              <Typography variant="body-xs" component="div">
                {formatCurrencyNoDecimals(currentValue)}
              </Typography>
            </Stack>
          </Stack>
          <Stack spacing={1}>
            {businesses.map((business) => (
              <EstateBusinessEntityCard
                key={`business-${business.id}`}
                business={business}
              />
            ))}
          </Stack>
        </>
      )}
      {aiOnboardingState.isModalOpen && (
        <AIOnboardingModal
          isOpen={aiOnboardingState.isModalOpen}
          onClose={() => aiOnboardingModal.closeModal()}
          householdId={householdId}
          variant={AIOnboardingModalVariant.BUSINESS_ENTITIES}
          initialSelectedSuggestion={
            aiOnboardingState.data?.initialSelectedSuggestion
          }
          suggestionsWhere={{
            idIn: businessEntitySuggestions.map((suggestion) => suggestion.id),
            acceptanceStatusIsNil: true,
          }}
          additionalReferenceSuggestions={additionalReferenceSuggestions}
          skipNavigationOnComplete={true}
        />
      )}
      {showCreateBusinessModal && (
        <CreateEntityShortFormModal
          isOpen={showCreateBusinessModal}
          onClose={() => setShowCreateBusinessModal(false)}
          householdId={householdId}
          limitEntityTypes={[...BUSINESS_ENTITY_TYPES]}
          heading="Create business"
          entityTypeLabel="Business type"
          buttonText="Create business"
        />
      )}
    </Stack>
  );
}
